import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useOrgId, useOrg, useOrgContext } from '@/app/session';
import { ArchiveNavigation } from '@/features/historical-correspondence/ArchiveNavigation';
import { Button, EmptyState, ErrorState, Icon, ImageryCredit, LinkButton, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import { validateSitePanelSearch, type SitePanelSearch } from '@/features/locations/search';
import { SitePanel } from '@/features/locations/SitePanel';
import { SiteThumb } from '@/features/locations/SiteThumb';
import type { PartyDrawerState } from '@/features/parties/PartyDrawers';
import {
  CONVERSATION_VIEWS,
  DEFAULT_CONVERSATION_VIEW,
  conversationListQuery,
  locationNameQuery,
  subjectWithoutLocation,
  toRow,
  useNeedsYouCount,
  type ConversationRow,
  type ConversationView,
} from './api';
import { useConversationActions, type ConversationActions } from './ConversationMenu';
import { FlowProvider, useFlow } from './flow';
import { RowTime, SiteLink, closedSitePanel } from './parts';
import { SignalDot, conversationSignal, type ConversationSignal } from './signal';
import { UnassignedMail } from './UnassignedMail';
import { shouldContinueMessages } from './thread-read-state';
import './conversations.css';
import './chat.css';

/** The list's view and query, and the site panel opened over whatever conversation page is showing. */
export interface ConversationsSearch extends SitePanelSearch {
  view?: ConversationView;
  q?: string;
}

const VIEWS = CONVERSATION_VIEWS.map((v) => v.id);

export function validateConversationsSearch(raw: Record<string, unknown>): ConversationsSearch {
  // The default view (To do) stays out of the URL.
  const view =
    typeof raw.view === 'string' && (VIEWS as string[]).includes(raw.view) && raw.view !== DEFAULT_CONVERSATION_VIEW ? (raw.view as ConversationView) : undefined;
  const q = typeof raw.q === 'string' && raw.q.trim() ? raw.q.trim().slice(0, 200) : undefined;
  return { view, q, ...validateSitePanelSearch(raw) };
}

/** Conversations: the people list on the left, the selected thread (or compose, or the overview) on the right. */
export function ConversationsLayout({ search, children }: { search: ConversationsSearch; children: ReactNode }) {
  return (
    <FlowProvider>
      <div className="mail">
        <ConversationList search={search} />
        <div className="mail-main">{children}</div>
      </div>
      {search.site ? <ConversationSitePanel locationId={search.site} party={{ contact: search.contact, company: search.company }} key={search.site} /> : null}
    </FlowProvider>
  );
}

/**
 * The site opened from inside a conversation (`?site=`), over the page so the thread, its scroll
 * and any draft stay put. Opening a person pushes, so Back walks the panel; closing clears it.
 */
function ConversationSitePanel({ locationId, party }: { locationId: string; party: PartyDrawerState }) {
  const navigate = useNavigate();
  return (
    <SitePanel
      locationId={locationId}
      party={party}
      onParty={(next) => void navigate({ to: '.', search: (s) => ({ ...s, contact: undefined, company: undefined, ...next }), resetScroll: false })}
      onClose={() => void navigate({ to: '.', search: closedSitePanel, resetScroll: false })}
    />
  );
}

function ConversationList({ search }: { search: ConversationsSearch }) {
  const orgId = useOrgId();
  const { user } = useOrgContext();
  const org = useOrg();
  const navigate = useNavigate({ from: '/app/conversations' });
  const { conversationId } = useParams({ strict: false }) as { conversationId?: string };
  const view = search.view ?? DEFAULT_CONVERSATION_VIEW;
  const list = useInfiniteQuery(conversationListQuery(orgId, view, search.q));
  const rows = useMemo(() => (list.data?.pages.flatMap((p) => p.items) ?? []).map(toRow), [list.data]);
  const needsCount = useNeedsYouCount(orgId);
  const actions = useConversationActions();
  const caughtUp = view === 'needs' && !search.q && list.isSuccess && rows.length === 0;

  // Keep the selected row in view when the selection changes.
  useEffect(() => {
    document.querySelector('.crow-current')?.scrollIntoView({ block: 'nearest' });
  }, [conversationId]);

  // The list keeps loading as it scrolls.
  const end = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError, isPending } = list;
  useEffect(() => { if (list.error) console.error('Conversation list unavailable', list.error); }, [list.error]);
  useEffect(() => {
    const el = end.current;
    if (!el || !shouldContinueMessages({ hasNextPage, isFetchingNextPage, isError, isPending })) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { root: el.closest('.mail-list-scroll'), rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError, isPending]);

  return (
    <section className="mail-list" aria-label="Conversations">
      <div className="mail-list-head">
        <nav className="segmented mail-views" aria-label="Conversation views">
          {CONVERSATION_VIEWS.map((v) => (
            <Link
              key={v.id}
              from="/app/conversations"
              to="."
              search={(s) => ({ ...s, view: v.id === DEFAULT_CONVERSATION_VIEW ? undefined : v.id })}
              aria-current={view === v.id ? 'page' : undefined}
            >
              {v.label}
              {v.id === 'needs' && needsCount ? <span className="mail-views-count num">{needsCount}</span> : null}
            </Link>
          ))}
        </nav>
        <ArchiveNavigation allowed={Boolean(user) && org.hasScope('parties:read') && org.hasScope('locations:read')} />
        <div className="mail-list-bar">
          <ListSummary view={view} rows={rows} more={Boolean(list.hasNextPage)} loading={list.isPending} />
          <LinkButton to="/app/conversations/new" search={true} variant="quiet" size="sm" icon="edit">
            Compose
          </LinkButton>
        </div>
        {search.q ? (
          <p className="mail-query">
            <Icon name="search" size={14} />
            <span>{search.q}</span>
            <Button size="sm" variant="quiet" onClick={() => void navigate({ to: '.', search: (s) => ({ ...s, q: undefined }) })}>
              Clear search
            </Button>
          </p>
        ) : null}
      </div>

      <div className="mail-list-scroll">
        {view === 'needs' && !search.q ? <UnassignedMail /> : null}
        {list.isError && <ErrorState error={list.error} onRetry={() => void (list.isFetchNextPageError ? list.fetchNextPage() : list.refetch())} />}
        {list.isPending ? (
          <ListSkeleton />
        ) : list.isError && rows.length === 0 ? null : caughtUp ? (
          <WaitingSuggestion selectedId={conversationId} actions={actions} />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            title={
              search.q
                ? 'No matches'
                : view === 'waiting'
                  ? 'No conversations awaiting reply'
                  : view === 'closed'
                    ? 'No closed conversations'
                    : 'No current conversations'
            }
            action={
              view === 'all' && !search.q ? (
                <LinkButton size="sm" variant="secondary" to="/app/conversations/new">
                  Start conversation
                </LinkButton>
              ) : undefined
            }
          />
        ) : (
          <ul className="crows">
            {rows.map((r) => (
              <li key={r.conversation.id}>
                <Row row={r} selected={r.conversation.id === conversationId} actions={actions} />
              </li>
            ))}
          </ul>
        )}
        {list.isSuccess && rows.length === 0 && !search.q ? <p className="small muted">Historical correspondence is separate from current conversations and is available to authorized private readers through Historical archive.</p> : null}
        <div ref={end} className="mail-more">
          {isFetchingNextPage ? <ListSkeleton rows={2} /> : null}
        </div>
        {rows.length ? <ImageryCredit className="mail-credit" /> : null}
      </div>
      {actions.element}
    </section>
  );
}

/** Signal label to count over the loaded rows, most urgent first: "Draft ready 3", "Reply needed 2". */
function signalCounts(rows: ConversationRow[]): Array<[string, number]> {
  const out = new Map<string, { n: number; rank: number }>();
  for (const r of rows) {
    const c = r.conversation;
    const s: Pick<ConversationSignal, 'label' | 'rank'> | null = conversationSignal(c) ?? (c.state === 'closed' ? { label: 'Closed', rank: 9 } : null);
    if (!s) continue;
    // "Sending 14:05" rows count together.
    const label = s.rank === 1 ? 'Sending' : s.label;
    const prev = out.get(label);
    out.set(label, { n: (prev?.n ?? 0) + 1, rank: s.rank });
  }
  return [...out.entries()].sort((a, b) => a[1].rank - b[1].rank).map(([label, { n }]) => [label, n]);
}

/**
 * The counts under the views, label then number: "Draft ready 3  Reply needed 2". In To do it
 * also tracks the session: "Done 2" with a bar of what was handled.
 */
function ListSummary({ view, rows, more, loading }: { view: ConversationView; rows: ConversationRow[]; more: boolean; loading: boolean }) {
  const { handled } = useFlow();
  if (loading) return <span className="mail-summary" />;
  const counts = signalCounts(rows);
  const left = rows.length;
  return (
    <span className="mail-summary">
      <span className="mail-counts" aria-live={view === 'needs' ? 'polite' : undefined}>
        {counts.map(([label, n]) => (
          <span key={label} className="mail-count">
            {label} <span className="num">{more ? `${n}+` : n}</span>
          </span>
        ))}
        {view === 'needs' && handled > 0 ? (
          <span className="mail-count">
            Done <span className="num">{handled}</span>
          </span>
        ) : null}
      </span>
      {view === 'needs' && handled > 0 ? (
        <span className="mail-progress" aria-hidden>
          <span style={{ '--done': `${Math.round((handled / Math.max(left + handled, 1)) * 100)}%` } as CSSProperties} />
        </span>
      ) : null}
    </span>
  );
}

/** To do is empty: say so, and list the conversations awaiting a reply instead. */
function WaitingSuggestion({ selectedId, actions }: { selectedId: string | undefined; actions: ConversationActions }) {
  const orgId = useOrgId();
  const waiting = useInfiniteQuery(conversationListQuery(orgId, 'waiting', undefined));
  const rows = useMemo(() => (waiting.data?.pages.flatMap((p) => p.items) ?? []).map(toRow), [waiting.data]);
  return (
    <div className="mail-caughtup">
      <p className="mail-caughtup-title">Nothing to do</p>
      {rows.length ? (
        <>
          <div className="mail-caughtup-head">
            <span>Awaiting reply</span>
            <Link from="/app/conversations" to="." search={(s) => ({ ...s, view: 'waiting' as const })}>
              Show all
            </Link>
          </div>
          <ul className="crows">
            {rows.slice(0, 8).map((r) => (
              <li key={r.conversation.id}>
                <Row row={r} selected={r.conversation.id === selectedId} actions={actions} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function ListSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div className="crows" aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="crow crow-skeleton">
          <span className="crow-thumb" />
          <span className="crow-main">
            <Skeleton width="55%" />
            <Skeleton width="85%" />
            <Skeleton width="40%" />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * A conversation row on a grid: the site's satellite thumbnail, then who and when, the subject, and
 * the state with the start of the latest message. The name is the row's link and covers it; the
 * thumbnail sits above that and opens the site panel.
 */
function Row({ row: r, selected, actions }: { row: ConversationRow; selected: boolean; actions: ConversationActions }) {
  const orgId = useOrgId();
  // Filled by the thumbnail once it nears the viewport (same query key); never fetched from here.
  const site = useQuery({ ...locationNameQuery(orgId, r.locationId ?? ''), enabled: false }).data;
  const c = r.conversation;
  const signal = conversationSignal(c);
  const status = signal ?? (c.state === 'closed' ? ({ label: 'Closed', tone: 'faint', rank: 9 } as const) : null);
  const subject = subjectWithoutLocation(r.subject, site?.name);
  return (
    <div
      className={cx('crow', r.unread && 'crow-unread', selected && 'crow-current', actions.menu.openKey === c.id && 'crow-menu')}
      {...actions.menu.surface(c.id, `Actions for ${r.name}`, () => actions.items(c))}
    >
      {r.locationId ? (
        <SiteLink locationId={r.locationId} className="crow-thumb" title={site ? `Open ${site.name}` : 'Open site'}>
          <SiteThumb id={r.locationId} city={null} width="100%" height="100%" />
        </SiteLink>
      ) : (
        <span className="crow-thumb site-thumb" title={c.source_history ? 'Company history; site links on entries' : 'No site linked'} />
      )}
      <span className="crow-main">
        <span className="crow-line">
          <Link
            to="/app/conversations/$conversationId"
            params={{ conversationId: c.id }}
            search={true}
            className="crow-name"
            aria-current={selected ? 'page' : undefined}
          >
            {r.unread ? <span className="visually-hidden">Unread. </span> : null}
            {r.name}
          </Link>
          {c.source_history && !r.at ? <span className="crow-time">Event date unknown</span> : <RowTime value={r.at} className="crow-time" />}
        </span>
        <span className="crow-subject">{subject}</span>
        {c.source_history ? <details className="crow-history-details"><summary>History / Notes</summary><span className="crow-subject">Recorded {c.source_history.source_kind ?? 'entry (kind unknown)'} · Recorder: {c.source_history.recorder ?? 'Unknown'}{c.source_history.source_created_at ? ` · Source entry created: ${c.source_history.source_created_at}` : ''}</span></details> : null}
        {c.source_history ? <span className="crow-source-links"><a href={`/app/companies/${encodeURIComponent(c.source_history.company_id)}`}>Linked company</a>{c.locations.map((link) => <SiteLink key={link.location_id} locationId={link.location_id}>Linked site</SiteLink>)}</span> : null}
        <span className="crow-line">
          {status ? <SignalDot signal={status} className="crow-state" /> : null}
          {r.gist !== r.subject ? <span className="crow-snippet">{r.gist}</span> : null}
        </span>
      </span>
    </div>
  );
}
